// src/pages/itinerary/ItineraryPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { startPreview, listenPreviewVersion } from '../../services/itinerary/previewApi';
import { ANALYTICS_EVENTS, trackEvent as trackFreemiumEvent } from '../../utils/freemium';
import { trackEvent as trackAnalyticsEvent, mergeItineraryAttribution, saveItineraryAttribution } from '../../utils/analytics';
import LoadingWithFactoids from '../../components/shared/LoadingWithFactoids';
import LayoutNoNav from '../../components/shared/LayoutNoNav';
import TravelProfileCard from '../../components/itinerary/TravelProfileCard';
import TripThesisCard from '../../components/itinerary/TripThesisCard';
import ItineraryDayCard from '../../components/itinerary/ItineraryDayCard';
import ItineraryUpgradeCta from '../../components/itinerary/ItineraryUpgradeCta';
import ItineraryPaymentSheet from '../../components/payments/ItineraryPaymentSheet';
import ItineraryActionsBar from '../../components/itinerary/ItineraryActionsBar';
import DayRegenerationSheet from '../../components/itinerary/DayRegenerationSheet';
import DayHistoryDrawer from '../../components/itinerary/DayHistoryDrawer';
import EditTokenSheet from '../../components/itinerary/EditTokenSheet';
import { requestItineraryPdf } from '../../services/pdfService';
import { retryFullItinerary } from '../../services/itinerary/regenerationApi';
import FeatureShowcase from '../../components/common/FeatureShowcase';
import { PREVIEW_PRICE_DISPLAY } from '../../config/pricing';
import { getGenerationFailureMessage } from './generationFailureMessage';
import { useItineraryOffer, refreshItineraryOffer } from 'alakai-aloha-shared/public-ui';
import { claimPilotItinerary, getPilotClaimToken } from '../../services/itinerary/pilotApi';
import { getItineraryPaymentEvent } from '../../utils/itineraryPaymentAnalytics';

// Removed unused Chip component
const FACTOID_WAIT_MESSAGE = 'Enjoy some Hawaii travel facts while you wait...';
const FACTOID_WAIT_MESSAGE_WITH_OR = `Or ${FACTOID_WAIT_MESSAGE.charAt(0).toLowerCase()}${FACTOID_WAIT_MESSAGE.slice(1)}`;

// Helper function to check if homebase data is valid and not placeholder values
const hasValidHomebase = (context) => {
  if (!context?.homebase) return false;
  
  // Check if homebase contains "undefined" or is just placeholder text
  const homebase = context.homebase.toLowerCase();
  return !homebase.includes('undefined') && 
         !homebase.includes('null') && 
         homebase !== 'home base' && 
         homebase.trim().length > 0;
};


function mergeDayLists(existing, incoming) {
  const map = new Map();
  if (Array.isArray(existing)) {
    existing.forEach((day) => {
      if (day && typeof day.dayNumber === 'number') {
        map.set(day.dayNumber, day);
      }
    });
  }
  if (Array.isArray(incoming)) {
    incoming.forEach((day) => {
      if (day && typeof day.dayNumber === 'number') {
        map.set(day.dayNumber, day);
      }
    });
  }
  return Array.from(map.values()).sort((a, b) => a.dayNumber - b.dayNumber);
}

function mergePreviewData(prev, next) {
  if (!next) return prev || null;
  if (!prev) return {
    ...next,
    days: Array.isArray(next?.days) ? next.days : [],
    previewDays: Array.isArray(next?.previewDays) ? next.previewDays : [],
    fullDays: Array.isArray(next?.fullDays) ? next.fullDays : [],
  };

  const merged = {
    ...prev,
    ...next,
    context: next?.context ?? prev?.context,
    thesis: next?.thesis ?? prev?.thesis,
    teasers: Array.isArray(next?.teasers) ? next.teasers : prev?.teasers,
    synthesisSnapshot: next?.synthesisSnapshot ?? prev?.synthesisSnapshot,
    meta: {
      ...(prev?.meta || {}),
      ...(next?.meta || {})
    }
  };

  merged.previewDays = mergeDayLists(prev?.previewDays, next?.previewDays);
  merged.fullDays = mergeDayLists(prev?.fullDays, next?.fullDays);
  const limitedDays = mergeDayLists(prev?.days, next?.days);
  merged.days = limitedDays.filter(day => typeof day?.dayNumber === 'number' && day.dayNumber <= 1).slice(0, 2);
  return merged;
}

function LimitReached({ payload, navigate, pilotActive }) {
  return (
    <div className="rounded-lg bg-gradient-to-br from-amber-500/20 via-amber-400/10 to-transparent border-2 border-amber-500/30 p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">⚠️</span>
        <div>
          <h3 className="text-white font-bold text-lg">Preview Limit Reached</h3>
          <p className="text-amber-200 text-sm">{payload?.message}</p>
        </div>
      </div>
      
      {payload?.previousVersions && payload.previousVersions.length > 0 && (
        <div className="mb-6">
          <div className="text-white font-medium mb-3">Your Previous Previews:</div>
          <div className="space-y-2">
            {payload.previousVersions.map((v, i) => (
              <button
                key={v.id || i}
                onClick={() => navigate(v.viewUrl ?? '/')}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{v.id}</span>
                  <span className="text-xs text-white/60">
                    {v.generatedAt ? new Date(v.generatedAt).toLocaleDateString() : 'Recent'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex flex-col md:flex-row gap-3">
        {pilotActive && (
          <p className="flex-1 text-white/80 text-sm">
            Open one of your previous previews above to get your complete itinerary free. No credit card required.
          </p>
        )}
        <button 
          onClick={() => navigate('/')}
          className="flex-1 btn-secondary"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}

const withVersionPrefix = (value) => {
  if (!value) return undefined;
  return value.startsWith('v') ? value : `v${value}`;
};

const versionToSlug = (value) => {
  if (!value) return '';
  return value.startsWith('v') ? value.slice(1) : value;
};

export default function ItineraryPage() {
  const { active: pilotActive, offer: itineraryOffer, loading: offerLoading } = useItineraryOffer();
  const { itineraryId, versionId: versionParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({ profile: false, days: {} });
  const [globalCollapsed, setGlobalCollapsed] = useState(false);
  const profileCardRef = useRef(null);
  const previewTrackedRef = useRef(false);
  const unlockTrackedRef = useRef(false);
  const pilotClaimAttemptRef = useRef(null);
  const pilotClaimInFlightRef = useRef(false);
  const fullReadyTrackedRef = useRef(null);
  const ctaRef = useRef(null);
  const checkoutOpenedRef = useRef(false);
  const [globalToggleOffset, setGlobalToggleOffset] = useState(24);
  const [stage, setStage] = useState(-1); // -1 rotator only, 0 context, 1 thesis, 2 arrival, 3 day1+preview, 4 CTA
  const [exitArmed, setExitArmed] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [copyToast, setCopyToast] = useState(null);
  const [retryingFullGeneration, setRetryingFullGeneration] = useState(false);
  const [fullRetryError, setFullRetryError] = useState(null);
  const [editToken, setEditToken] = useState(null);
  const [editTokenSheetOpen, setEditTokenSheetOpen] = useState(false);
  const [regenerationSheetOpen, setRegenerationSheetOpen] = useState(false);
  const [regenerationDayIndex, setRegenerationDayIndex] = useState(null);
  const [localRegeneratingDayIndex, setLocalRegeneratingDayIndex] = useState(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyDayIndex, setHistoryDayIndex] = useState(null);
  const interviewId = itineraryId; // TODO: harmonize naming when backend differentiates itinerary vs interview ids
  const versionId = withVersionPrefix(versionParam);
  const versionSlug = useMemo(() => versionToSlug(versionId), [versionId]);
  // Optional pre-confirmation params from Scenario 5/6
  const confirmArea = searchParams.get('confirmArea');
  const confirmRegion = searchParams.get('confirmRegion');
  const confirmScenario = searchParams.get('scenario') || undefined;
  const openCheckoutParam = searchParams.get('openCheckout');
  const shouldAutoOpenCheckout = openCheckoutParam === 'true' || openCheckoutParam === '1';

  useEffect(() => {
    if (!itineraryId || !versionId) {
      setEditToken(null);
      return;
    }

    const storageKey = `itineraryEditToken:${itineraryId}:${versionId}`;
    const hash = typeof window !== 'undefined' ? window.location.hash || '' : '';
    if (hash.startsWith('#edit=')) {
      const token = hash.slice('#edit='.length).trim();
      if (token) {
        setEditToken(token);
        try {
          window.sessionStorage.setItem(storageKey, token);
        } catch {
          // ignore storage errors
        }
      }

      try {
        const clean = `${window.location.pathname}${window.location.search}`;
        window.history.replaceState(null, '', clean);
      } catch {
        // ignore history errors
      }
      return;
    }

    try {
      const saved = window.sessionStorage.getItem(storageKey);
      if (saved) setEditToken(saved);
      else setEditToken(null);
    } catch {
      setEditToken(null);
    }
  }, [itineraryId, versionId]);

  useEffect(() => {
    setRegenerationSheetOpen(false);
    setRegenerationDayIndex(null);
    setLocalRegeneratingDayIndex(null);
    setHistoryDrawerOpen(false);
    setHistoryDayIndex(null);
  }, [itineraryId, versionId]);

  // Subscribe to an existing version when versionId is present
  useEffect(() => {
    if (!versionId) return;
    let unsub;
    try {
      trackFreemiumEvent(ANALYTICS_EVENTS.VIEWER_OPEN, { page: 'itinerary_preview', interviewId, versionId });
      unsub = listenPreviewVersion(interviewId, versionId, (payload, first) => {
        setData(prev => mergePreviewData(prev, payload));
        if (first) setLoading(false);
      }, (err) => {
        setError(err?.message || 'Failed to stream itinerary');
        setLoading(false);
      });
    } catch (e) {
      setError(e?.message || 'Failed to stream itinerary');
      setLoading(false);
    }
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [interviewId, versionId]);

  useEffect(() => {
    previewTrackedRef.current = false;
    unlockTrackedRef.current = false;
  }, [interviewId, versionId]);

  // Kick off generation when no versionId; optionally confirm homebase first
  useEffect(() => {
    if (versionId) return;
    let cancelled = false;

    (async () => {
      try {
        trackFreemiumEvent(ANALYTICS_EVENTS.VIEWER_OPEN, { page: 'itinerary_preview', interviewId, versionId: 'new' });

        // If Scenario 5/6 provided a confirmed area/region, write that first
        if (confirmArea && confirmRegion) {
          try {
            const response = await fetch('https://us-central1-alakaialohaadventures-782c9.cloudfunctions.net/homebaseConfirm', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                data: {
                  interviewId,
                  area: confirmArea,
                  region: confirmRegion,
                  scenario: confirmScenario
                }
              })
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            await response.json();
          } catch (e) {
            // Log, but proceed to generation to avoid blocking UX
            console.warn('[ItineraryPreview] homebaseConfirm failed or delayed:', e?.message || e);
          }
        }

        if (cancelled) return;
        const start = await startPreview(interviewId);
        if (cancelled) return;

        if (start?.success) {
          const slug = versionToSlug(start.versionId);
          navigate(`/itinerary/${interviewId}/v${slug}`, { replace: true });
          return;
        }
        if (start?.limitReached) {
          setLimitInfo(start);
        } else {
          setError(start?.message || 'Failed to start preview');
        }
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load itinerary preview');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [interviewId, versionId, confirmArea, confirmRegion, confirmScenario]); // eslint-disable-line react-hooks/exhaustive-deps

  // Error and limit handling will return after hooks are declared

  const context = data?.context;
  const rawDays = data?.days;
  const days = useMemo(() => (Array.isArray(rawDays) ? rawDays : []), [rawDays]);
  const rawPreviewDays = data?.previewDays;
  const previewDays = useMemo(() => (Array.isArray(rawPreviewDays) ? rawPreviewDays : days), [rawPreviewDays, days]);
  const rawFullDays = data?.fullDays;
  const fullDays = useMemo(
    () => (Array.isArray(rawFullDays) && rawFullDays.length > 0 ? rawFullDays : previewDays),
    [rawFullDays, previewDays]
  );
  const allDays = useMemo(() => (fullDays.length > 0 ? fullDays : previewDays), [fullDays, previewDays]);
  const extraDays = useMemo(
    () => allDays.filter(day => typeof day?.dayNumber === 'number' && day.dayNumber >= 2),
    [allDays]
  );
  const arrival = useMemo(() => days.find(d => d.dayNumber === 0), [days]);
  const day1 = useMemo(() => days.find(d => d.dayNumber === 1), [days]);
  const fullAccess = (data?.meta?.accessLevel || '').toLowerCase() === 'full';
  const rawGenerationStatus = (data?.meta?.generationStatus || '').toLowerCase();
  const plannerFullStatus = (data?.meta?.plannerFullStatus || '').toLowerCase();
  const teaserCount = Array.isArray(data?.teasers) ? data.teasers.length : 0;
  const fullCoverage = fullAccess && fullDays.length >= Math.max(2, teaserCount);
  let generationStatus = rawGenerationStatus || plannerFullStatus || 'preview';
  if (generationStatus === 'preview' && plannerFullStatus === 'in-progress') {
    generationStatus = 'running';
  }
  if (generationStatus !== 'complete' && fullCoverage) {
    generationStatus = 'complete';
  }
  const generationError = data?.meta?.generationError;
  const generationFailed = generationStatus === 'error'
    || (data?.meta?.status || '').toLowerCase() === 'error';
  const failedDayLabel = typeof generationError === 'string'
    ? generationError.match(/failed for ([^:]+):/i)?.[1]
    : null;
  const generationFailureMessage = getGenerationFailureMessage({ fullAccess, failedDayLabel });

  useEffect(() => {
    if (!shouldAutoOpenCheckout || checkoutOpenedRef.current || offerLoading || pilotActive || !itineraryOffer) return;
    if (!versionId || fullAccess) return;
    checkoutOpenedRef.current = true;
    trackAnalyticsEvent('itinerary_unlock_started', mergeItineraryAttribution({
      interview_id: interviewId,
      itinerary_version: versionId,
      source: 'auto_open_checkout',
    }));
    setPaymentSheetOpen(true);
  }, [shouldAutoOpenCheckout, versionId, fullAccess, interviewId, offerLoading, pilotActive, itineraryOffer]);

  useEffect(() => {
    if (pilotActive) setPaymentSheetOpen(false);
  }, [pilotActive]);

  useEffect(() => {
    if (stage < 4) return;
    if (!ctaRef.current) return;
    const hash = typeof window !== 'undefined' ? (window.location.hash || '').toLowerCase() : '';
    const shouldScrollToCta = shouldAutoOpenCheckout || hash === '#cta' || hash === '#unlock' || hash === '#checkout';
    if (!shouldScrollToCta) return;
    ctaRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [stage, shouldAutoOpenCheckout]);

  // Calculate dynamic trip duration (user's total trip length)
  const totalTripDays = Array.isArray(data?.teasers) ? data.teasers.length : 0;
  const daysShown = 2; // Arrival + Day 1

  // Check if trip exceeds our 15-day planning limit
  const MAX_ITINERARY_DAYS = 15;
  const isTripCapped = totalTripDays > MAX_ITINERARY_DAYS;
  const plannedDays = isTripCapped ? MAX_ITINERARY_DAYS : totalTripDays;
  const unplannedDays = isTripCapped ? totalTripDays - MAX_ITINERARY_DAYS : 0;

  // Progressive gating (6s boundaries)
  const showGenerationLoader = fullAccess && generationStatus !== 'complete' && generationStatus !== 'error';
  const showGlobalToggle = fullAccess && stage >= 4 && !unlocking && !showGenerationLoader;
  const showItineraryActions = fullAccess && generationStatus === 'complete';
  const editPolicy = (data?.meta?.editPolicy || '').toLowerCase();

  // Preview regeneration state (for preview users before purchase)
  const previewRegenerationMax = data?.meta?.previewRegeneration?.maxAllowed || 5;
  const previewRegenerationUsed = data?.meta?.previewRegeneration?.totalUsed || 0;
  const previewRegenerationsRemaining = Math.max(0, previewRegenerationMax - previewRegenerationUsed);

  // Paid regeneration state (for paid users after purchase)
  const regenerationMax = data?.meta?.regeneration?.maxAllowed || 20;
  const regenerationUsed = data?.meta?.regeneration?.totalUsed || 0;
  const regenerationsRemaining = Math.max(0, regenerationMax - regenerationUsed);

  // Preview mode users can iterate once a preview day is present; paid users need token
  const isPreviewMode = !fullAccess;
  const previewGenerationComplete = (data?.meta?.plannerPreviewStatus || '').toLowerCase() === 'complete' || (data?.meta?.status || '').toLowerCase() === 'complete';
  const previewDaysReady = Boolean(arrival || day1);
  const canIteratePreview = isPreviewMode && previewRegenerationsRemaining > 0;
  const canIteratePaid = showItineraryActions && editPolicy === 'token_required' && !!editToken;
  const canIterate = canIteratePreview || canIteratePaid;
  const canEnterToken = showItineraryActions && editPolicy === 'token_required' && !editToken;

  // Active regeneration counts based on mode
  const activeRegenerationsRemaining = fullAccess ? regenerationsRemaining : previewRegenerationsRemaining;
  const canRegenerate = canIterate && activeRegenerationsRemaining > 0;
  const showPreviewRegenLimitCta = isPreviewMode && previewDaysReady && previewRegenerationsRemaining === 0;
  const regeneratingDayIndexServer = typeof data?.meta?.regenerating?.dayIndex === 'number' ? data.meta.regenerating.dayIndex : null;
  const publicOrigin = process.env.REACT_APP_PUBLIC_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const viewOnlyUrl = useMemo(() => {
    if (!publicOrigin || !itineraryId || !versionId) return null;
    return `${publicOrigin}/itinerary/${itineraryId}/${versionId}`;
  }, [publicOrigin, itineraryId, versionId]);
  const editableUrl = useMemo(() => {
    if (!canIterate || !viewOnlyUrl || !editToken) return null;
    try {
      const url = new URL(viewOnlyUrl);
      url.hash = `edit=${editToken}`;
      return url.toString();
    } catch {
      return `${viewOnlyUrl}#edit=${editToken}`;
    }
  }, [canIterate, editToken, viewOnlyUrl]);

  const itineraryActions = useMemo(() => {
    const base = [
      { key: 'save', label: 'Save Link', shortLabel: 'Save' },
      { key: 'share', label: 'Share', shortLabel: 'Share' },
      { key: 'pdf', label: 'Download PDF', shortLabel: 'Download' },
      { key: 'calendar', label: 'Add to Calendar', shortLabel: 'Calendar' },
    ];
    if (canEnterToken) {
      base.unshift({ key: 'enter_edit', label: 'Enter Edit Token', shortLabel: 'Edit' });
    }
    return base;
  }, [canEnterToken]);

  useEffect(() => {
    if (!copyToast) return undefined;
    const timeout = setTimeout(() => setCopyToast(null), 2000);
    return () => clearTimeout(timeout);
  }, [copyToast]);

  const handleEnableEditing = useCallback((token) => {
    if (!itineraryId || !versionId) return;
    const storageKey = `itineraryEditToken:${itineraryId}:${versionId}`;
    setEditToken(token);
    try {
      window.sessionStorage.setItem(storageKey, token);
    } catch {
      // ignore storage errors
    }
    setCopyToast({ kind: 'success', message: 'Editing enabled for this session.' });
  }, [itineraryId, versionId]);

  const handleRegenerationStart = useCallback(({ dayIndex, dayTitle }) => {
    const label = dayTitle || (typeof dayIndex === 'number' ? `Day ${dayIndex + 1}` : 'day');
    if (typeof dayIndex === 'number') {
      setLocalRegeneratingDayIndex(dayIndex);
    }
    setCopyToast({ kind: 'info', message: `Regenerating ${label}…` });
  }, []);

  const handleRegenerationSuccess = useCallback((result) => {
    const label = typeof result?.dayIndex === 'number'
      ? (result.dayIndex === 0 ? 'Arrival Day' : `Day ${result.dayIndex + 1}`)
      : 'Day';
    setLocalRegeneratingDayIndex(null);
    setCopyToast({ kind: 'success', message: `${label} updated.` });
  }, []);

  const handleRegenerationError = useCallback((err) => {
    const previewLimitReached = err?.details?.previewLimitReached;
    const message = previewLimitReached
      ? 'Preview regen limit reached — unlock the full itinerary for 20 more regenerations.'
      : err?.message || 'Failed to regenerate day. Please try again.';
    setLocalRegeneratingDayIndex(null);
    setCopyToast({ kind: 'error', message });
  }, []);

  const handleRetryFullGeneration = useCallback(async () => {
    if (!itineraryId || !versionId || retryingFullGeneration) return;
    if (editPolicy === 'token_required' && !editToken) {
      setFullRetryError('Enter the edit token from your receipt, then retry again.');
      setEditTokenSheetOpen(true);
      return;
    }

    setRetryingFullGeneration(true);
    setFullRetryError(null);
    try {
      await retryFullItinerary({itineraryId, versionId, editToken});
      setCopyToast({kind: 'info', message: 'Retry queued. Your completed days are safe.'});
    } catch (err) {
      setFullRetryError(err?.message || 'We could not queue the retry. Please try again.');
    } finally {
      setRetryingFullGeneration(false);
    }
  }, [editPolicy, editToken, itineraryId, retryingFullGeneration, versionId]);

  const isDayRegenerating = useCallback((dayNumber) => {
    if (typeof dayNumber !== 'number') return false;
    return dayNumber === regeneratingDayIndexServer || dayNumber === localRegeneratingDayIndex;
  }, [localRegeneratingDayIndex, regeneratingDayIndexServer]);

  const handleOpenRegeneration = useCallback((dayNumber) => {
    if (!canIterate) return;
    if (typeof dayNumber !== 'number') return;
    setRegenerationDayIndex(dayNumber);
    setRegenerationSheetOpen(true);
  }, [canIterate]);

  const handleOpenHistory = useCallback((dayNumber) => {
    if (!canIterate) return;
    if (typeof dayNumber !== 'number') return;
    setHistoryDayIndex(dayNumber);
    setHistoryDrawerOpen(true);
  }, [canIterate]);

  const activeRegenerationDay = useMemo(() => {
    if (typeof regenerationDayIndex !== 'number') return null;
    return allDays.find(d => d?.dayNumber === regenerationDayIndex) || null;
  }, [allDays, regenerationDayIndex]);

  const activeHistoryDay = useMemo(() => {
    if (typeof historyDayIndex !== 'number') return null;
    return allDays.find(d => d?.dayNumber === historyDayIndex) || null;
  }, [allDays, historyDayIndex]);

  const handleItineraryAction = useCallback((actionKey) => {
    trackAnalyticsEvent('itinerary_action', mergeItineraryAttribution({
      interview_id: itineraryId,
      itinerary_version: versionId,
      action: actionKey,
      pilot_id: data?.meta?.unlockSource?.pilotId,
      access_method: data?.meta?.unlockSource?.type || 'paid',
    }));
    switch (actionKey) {
      case 'enter_edit':
        if (!canEnterToken) break;
        setEditTokenSheetOpen(true);
        break;
      case 'save':
        if (!versionSlug) break;
        navigate(`/itinerary/saved/${itineraryId}/${versionSlug}`);
        break;
      case 'share':
        if (!versionSlug) break;
        navigate(`/itinerary/share/${itineraryId}/${versionSlug}`);
        break;
      case 'pdf':
        if (downloadingPdf) break;
        if (!itineraryId || !versionId) break;
        setPdfError(null);
        setDownloadingPdf(true);
        requestItineraryPdf(itineraryId, versionId)
          .then((result) => {
            if (!result.success) {
              setPdfError(result.error || 'Unable to download the PDF right now.');
            }
          })
          .catch((err) => {
            console.error('[ItineraryPage] pdf download failed', err);
            setPdfError('Unable to download the PDF right now.');
          })
          .finally(() => {
            setDownloadingPdf(false);
          });
        break;
      case 'calendar':
        if (!versionSlug) break;
        navigate(`/itinerary/calendar/${itineraryId}/${versionSlug}`);
        break;
      default:
        break;
    }
  }, [navigate, itineraryId, versionSlug, versionId, downloadingPdf, canEnterToken, data?.meta?.unlockSource]);
  const globalToggleLabel = globalCollapsed ? 'Expand all sections' : 'Collapse all sections';
  const readyContext = !!context;
  const readyThesis = !!data?.thesis;
  const readyArrival = !!arrival;
  const readyDay1 = !!day1;
  const plannerPreviewStatus = (data?.meta?.plannerPreviewStatus || '').toLowerCase();
  const plannerOverallStatus = (data?.meta?.status || '').toLowerCase();
  const previewComplete = plannerPreviewStatus === 'complete' || plannerOverallStatus === 'complete';

  // Don't auto-collapse context - let user control visibility
  // The Travel Profile should remain expanded when it first appears

  // Arm rotator exit once the next section is ready (to finish current 6s)
  useEffect(() => {
    const nextReady = stage === -1 ? readyContext
      : stage === 0 ? readyThesis
      : stage === 1 ? readyArrival
      : stage === 2 ? readyDay1
      : stage === 3 ? true
      : false;
    if ((stage >= -1 && stage <= 3) && nextReady && !exitArmed) setExitArmed(true);
  }, [stage, readyContext, readyThesis, readyArrival, readyDay1, exitArmed]);

  const handleRotatorExit = () => {
    setStage(prev => Math.min(prev + 1, 4));
    setExitArmed(false);
  };

  useEffect(() => {
    if (!previewComplete) return;

    let target = -1;
    if (readyContext) target = 0;
    if (readyThesis) target = 1;
    if (readyArrival) target = 2;
    if (readyDay1) target = 3;
    if (readyDay1) target = 4;

    if (target > stage) {
      setStage(target);
      setExitArmed(false);
    }
  }, [readyContext, readyThesis, readyArrival, readyDay1, plannerPreviewStatus, plannerOverallStatus, stage, previewComplete]);

  useEffect(() => {
    if (!previewComplete || !interviewId || previewTrackedRef.current) return;
    const eventPayload = mergeItineraryAttribution({
      interview_id: interviewId,
      itinerary_version: versionId,
      preview_status: plannerPreviewStatus,
      access_level: data?.meta?.accessLevel,
    });
    trackAnalyticsEvent('itinerary_preview_ready', eventPayload);
    saveItineraryAttribution(eventPayload);
    previewTrackedRef.current = true;
  }, [previewComplete, interviewId, versionId, plannerPreviewStatus, data?.meta?.accessLevel]);

  const handlePilotClaim = useCallback(async () => {
    if (!interviewId || !versionId || pilotClaimInFlightRef.current) return;
    pilotClaimInFlightRef.current = true;
    setCheckoutError(null);
    setUnlocking(true);
    setPaymentSheetOpen(false);
    try {
      const result = await claimPilotItinerary({ itineraryId: interviewId, versionId });
      if (result.editToken) handleEnableEditing(result.editToken);
      if (!result.alreadyUnlocked) {
        trackAnalyticsEvent('itinerary_pilot_unlock', mergeItineraryAttribution({
          interview_id: interviewId,
          itinerary_version: versionId,
          pilot_id: result.pilotId,
          value: 0,
          currency: 'USD',
          access_method: 'free_pilot',
        }));
      }
    } catch (err) {
      setCheckoutError(err?.message || 'We could not unlock your free itinerary. Please try again.');
      trackAnalyticsEvent('flow_error', mergeItineraryAttribution({
        interview_id: interviewId,
        itinerary_version: versionId,
        flow: 'itinerary_pilot',
        error_code: err?.code || 'unknown',
      }));
      void refreshItineraryOffer();
    } finally {
      pilotClaimInFlightRef.current = false;
      setUnlocking(false);
    }
  }, [interviewId, versionId, handleEnableEditing]);

  useEffect(() => {
    if (!previewComplete || !versionId || generationFailed) return;
    const ownPilotGrant = fullAccess && data?.meta?.unlockSource?.type === 'free_pilot'
      && getPilotClaimToken(interviewId, versionId);
    if ((!pilotActive || fullAccess) && !ownPilotGrant) return;
    const key = `${interviewId}:${versionId}`;
    if (pilotClaimAttemptRef.current === key) return;
    pilotClaimAttemptRef.current = key;
    void handlePilotClaim();
  }, [previewComplete, versionId, generationFailed, fullAccess, data?.meta?.unlockSource?.type,
    pilotActive, interviewId, handlePilotClaim]);

  useEffect(() => {
    if (!showItineraryActions || !versionId) return;
    const key = `${interviewId}:${versionId}`;
    if (fullReadyTrackedRef.current === key) return;
    fullReadyTrackedRef.current = key;
    trackAnalyticsEvent('itinerary_full_ready', mergeItineraryAttribution({
      interview_id: interviewId,
      itinerary_version: versionId,
      pilot_id: data?.meta?.unlockSource?.pilotId,
      access_method: data?.meta?.unlockSource?.type || 'paid',
    }));
  }, [showItineraryActions, interviewId, versionId, data?.meta?.unlockSource]);

  const handleUpgradeClick = async () => {
    trackFreemiumEvent(ANALYTICS_EVENTS.UPGRADE_CLICK, { source: 'preview_cta' });
    if (!versionId) {
      setCheckoutError('We need a saved itinerary version before you can unlock the full plan. Please try again in a moment.');
      return;
    }
    setCheckoutError(null);
    const currentOffer = await refreshItineraryOffer();
    if (!currentOffer) {
      setCheckoutError('We could not confirm the current offer. Please try again in a moment.');
      return;
    }
    if (currentOffer.active) {
      await handlePilotClaim();
      return;
    }
    trackAnalyticsEvent('itinerary_unlock_started', mergeItineraryAttribution({
      interview_id: interviewId,
      itinerary_version: versionId,
      source: 'preview_cta',
    }));
    setPaymentSheetOpen(true);
  };

  const handlePaymentClose = () => {
    setPaymentSheetOpen(false);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    setCheckoutError(null);
    setUnlocking(true);
    if (paymentIntent?.editToken) {
      handleEnableEditing(paymentIntent.editToken);
    }
    trackFreemiumEvent(ANALYTICS_EVENTS.UPGRADE_CONFIRMED, {
      itineraryId: interviewId,
      versionId,
      paymentIntentId: paymentIntent?.id,
      status: paymentIntent?.status,
    });
    const paymentEvent = getItineraryPaymentEvent(paymentIntent);
    const eventPayload = mergeItineraryAttribution({
      interview_id: interviewId,
      itinerary_version: versionId,
      payment_intent_id: paymentIntent?.id,
      payment_status: paymentIntent?.status,
      // Google Ads conversion tracking parameters
      value: paymentEvent.value,
      currency: paymentIntent?.currency?.toUpperCase() || 'USD',
      transaction_id: paymentIntent?.id || `${interviewId}_${versionId}_${Date.now()}`,
    });
    trackAnalyticsEvent(paymentEvent.eventName, eventPayload);
    saveItineraryAttribution(eventPayload);
  };

  const handlePaymentError = (err) => {
    if (err?.details?.code === 'free_pilot_active') {
      setPaymentSheetOpen(false);
      void refreshItineraryOffer();
      return;
    }
    const message = err?.message || 'Checkout failed. Please try again.';
    setCheckoutError(message);
  };

  const applyCollapsedState = useCallback((collapsed) => {
    const dayState = new Map();
    const collect = (list) => {
      if (!Array.isArray(list)) return;
      list.forEach((day) => {
        if (day && typeof day.dayNumber === 'number') {
          dayState.set(day.dayNumber, collapsed);
        }
      });
    };
    collect(days);
    collect(extraDays);
    setCollapsedSections({
      profile: collapsed,
      days: Object.fromEntries(dayState),
    });
  }, [days, extraDays]);

  const toggleProfileCollapsed = () => {
    setCollapsedSections((prev) => {
      const next = !prev.profile;
      if (globalCollapsed && next === false) setGlobalCollapsed(false);
      return {
        ...prev,
        profile: next,
      };
    });
  };

  const toggleDayCollapsed = (dayNumber) => {
    if (typeof dayNumber !== 'number') return;
    setCollapsedSections((prev) => {
      const current = prev?.days?.[dayNumber] || false;
      const next = !current;
      if (globalCollapsed && next === false) setGlobalCollapsed(false);
      return {
        ...prev,
        days: {
          ...(prev.days || {}),
          [dayNumber]: next,
        },
      };
    });
  };

  const handleGlobalToggle = () => {
    setGlobalCollapsed((prev) => {
      const next = !prev;
      applyCollapsedState(next);
      return next;
    });
  };

  useEffect(() => {
    if (unlocking && fullAccess) {
      setUnlocking(false);
    }
  }, [fullAccess, unlocking]);

  useEffect(() => {
    if (fullAccess) {
      setCheckoutError(null);
    }
  }, [fullAccess]);

  useEffect(() => {
    setCollapsedSections({ profile: false, days: {} });
    setGlobalCollapsed(false);
  }, [versionId]);

  useEffect(() => {
    if (globalCollapsed) {
      applyCollapsedState(true);
    }
  }, [globalCollapsed, applyCollapsedState]);

  useEffect(() => {
    if (!fullAccess || !interviewId || unlockTrackedRef.current) return;
    const eventPayload = mergeItineraryAttribution({
      interview_id: interviewId,
      itinerary_version: versionId,
      access_level: 'full',
      pilot_id: data?.meta?.unlockSource?.pilotId,
      access_method: data?.meta?.unlockSource?.type || 'paid',
    });
    trackAnalyticsEvent('itinerary_unlocked', eventPayload);
    saveItineraryAttribution(eventPayload);
    unlockTrackedRef.current = true;
  }, [fullAccess, interviewId, versionId, data?.meta?.unlockSource]);

  useEffect(() => {
    if (!showGlobalToggle) return undefined;

    const computeOffset = () => {
      if (!profileCardRef.current) {
        setGlobalToggleOffset(24);
        return;
      }
      const rect = profileCardRef.current.getBoundingClientRect();
      const offset = Math.max(window.innerWidth - rect.right, 16);
      setGlobalToggleOffset(offset);
    };

    const frame = requestAnimationFrame(computeOffset);
    window.addEventListener('resize', computeOffset);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', computeOffset);
    };
  }, [showGlobalToggle]);

  // Handle loading, error, and limit states
  if (loading && !data && !error && !limitInfo) {
    return (
      <LayoutNoNav showHeader={true} headerExitRoute="/">
        <div className="min-h-[50vh] flex items-center justify-center">
          <LoadingWithFactoids
            progressMessages={["Synthesizing your travel profile..."]}
          />
        </div>
      </LayoutNoNav>
    );
  }

  if (error) return (
    <LayoutNoNav showHeader={true} headerExitRoute="/">
      <div className="min-h-[50vh] flex items-center justify-center text-red-300">{error}</div>
    </LayoutNoNav>
  );
  
  if (limitInfo) return (
    <LayoutNoNav showHeader={true} headerExitRoute="/">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <LimitReached payload={limitInfo} navigate={navigate} pilotActive={pilotActive} />
      </div>
    </LayoutNoNav>
  );

  return (
    <>
      <Helmet>
        <title>Itinerary | Alaka'i Aloha</title>
      </Helmet>
      <LayoutNoNav showHeader={true} headerExitRoute="/">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">

      {showItineraryActions && (
        <div className="mb-5">
          <div className="flex justify-end">
            <ItineraryActionsBar
              labelMode="short"
              variant="subtle"
              actions={itineraryActions}
              onAction={handleItineraryAction}
              busyActionKey={downloadingPdf ? 'pdf' : undefined}
              busyLabel="Preparing…"
            />
          </div>
          {pdfError && (
            <div className="mt-2 text-xs md:text-sm text-amber-300" role="alert">{pdfError}</div>
          )}
        </div>
      )}

      {generationFailed && (
        <div className="mb-6 rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert">
          <p>
            {generationFailureMessage}
          </p>
          {fullRetryError && <p className="mt-2 text-red-100">{fullRetryError}</p>}
          {fullAccess && (
            <button
              type="button"
              className="mt-3 rounded-md border border-red-200/40 bg-white/10 px-3 py-2 font-semibold text-white transition-colors hover:bg-white/20 disabled:cursor-wait disabled:opacity-60"
              onClick={handleRetryFullGeneration}
              disabled={retryingFullGeneration}
            >
              {retryingFullGeneration ? 'Queueing retry…' : 'Retry from unfinished day'}
            </button>
          )}
          {!fullAccess && (
            <button
              type="button"
              className="mt-3 rounded-md border border-red-200/40 bg-white/10 px-3 py-2 font-semibold text-white transition-colors hover:bg-white/20"
              onClick={() => navigate(`/interview/review/${interviewId}`)}
            >
              Return to interview
            </button>
          )}
        </div>
      )}

      {/* Stage -1: Rotator only (initially) */}
      {stage === -1 && !generationFailed && (
        <div className="min-h-[40vh] flex items-center justify-center">
          <LoadingWithFactoids
            shouldSoftExit={exitArmed}
            onSoftExitComplete={handleRotatorExit}
            progressMessages={["Synthesizing your travel profile..."]}
          />
        </div>
      )}

      {/* Stage 0: Traveler Context */}
      {stage >= 0 && context && hasValidHomebase(context) && (
        <div className="mb-6" ref={profileCardRef}>
          <TravelProfileCard
            context={context}
            isCollapsed={collapsedSections.profile}
            onToggle={toggleProfileCollapsed}
            canEdit={!fullAccess}
          />
        </div>
      )}
      {!generationFailed && (stage === 0 || (stage >= 0 && context && !hasValidHomebase(context))) && (
        <div className="py-4">
          <LoadingWithFactoids
            shouldSoftExit={exitArmed}
            onSoftExitComplete={handleRotatorExit}
            progressMessages={[
              "Pulling together a trip overview...",
              "This usually takes about a minute...",
              FACTOID_WAIT_MESSAGE
            ]}
          />
        </div>
      )}

      {/* Stage 1: Trip Thesis */}
      {stage >= 1 && (
        <TripThesisCard
          thesis={data?.thesis}
          teasers={data?.teasers}
          synthesisSnapshot={data?.synthesisSnapshot}
          homebase={context?.homebase}
        />
      )}
      {stage === 1 && !generationFailed && (
        <div className="py-4">
          <LoadingWithFactoids
            shouldSoftExit={exitArmed}
            onSoftExitComplete={handleRotatorExit}
            progressMessages={[
              "Planning your arrival day...",
              "This usually takes a couple of minutes...",
              FACTOID_WAIT_MESSAGE
            ]}
          />
        </div>
      )}

      {/* Stage 2: Arrival Day */}
      {stage >= 2 && arrival && (
        <ItineraryDayCard
          day={arrival}
          isCollapsed={collapsedSections.days?.[arrival.dayNumber] || false}
          onToggle={() => toggleDayCollapsed(arrival.dayNumber)}
          canEdit={canIterate}
          canRegenerate={canRegenerate}
          isRegenerating={isDayRegenerating(arrival.dayNumber)}
          onRegenerate={handleOpenRegeneration}
          onViewHistory={handleOpenHistory}
        />
      )}
      {stage === 2 && !generationFailed && (
        <div className="py-4">
          <LoadingWithFactoids
            shouldSoftExit={exitArmed}
            onSoftExitComplete={handleRotatorExit}
            progressMessages={[
              "Planning your first full day...",
              "This usually takes a couple of minutes...",
              FACTOID_WAIT_MESSAGE
            ]}
          />
        </div>
      )}

      {/* Stage 3: Day 1 + Booking Preview */}
      {stage >= 3 && day1 && (
        <div className="mb-6">
          <ItineraryDayCard
            day={day1}
            isCollapsed={collapsedSections.days?.[day1.dayNumber] || false}
            onToggle={() => toggleDayCollapsed(day1.dayNumber)}
            canEdit={canIterate}
            canRegenerate={canRegenerate}
            isRegenerating={isDayRegenerating(day1.dayNumber)}
            onRegenerate={handleOpenRegeneration}
            onViewHistory={handleOpenHistory}
          />
        </div>
      )}
      {stage >= 3 && showPreviewRegenLimitCta && (
        <div className="mb-6 rounded-lg border border-amber-400/40 bg-amber-500/10 px-5 py-4 text-amber-100">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold">Preview regen limit reached</h3>
              <p className="text-sm text-amber-100/90">
                Unlock the full itinerary to get 20 more regenerations and the rest of your trip.
              </p>
            </div>
            <button type="button" className="btn-primary-cta" onClick={handleUpgradeClick}>
              {pilotActive ? 'Get My Complete Itinerary Free' : PREVIEW_PRICE_DISPLAY}
            </button>
          </div>
        </div>
      )}
      {stage === 3 && !generationFailed && (
        <div className="py-4">
          <LoadingWithFactoids
            shouldSoftExit={exitArmed}
            onSoftExitComplete={handleRotatorExit}
            progressMessages={[
              "Planning your first full day...",
              "This usually takes a couple of minutes...",
              FACTOID_WAIT_MESSAGE
            ]}
          />
        </div>
      )}

      {/* Stage 4: CTA */}
      {stage >= 4 && (
        <div ref={ctaRef} id="itinerary-cta">
          {!fullAccess ? (
            <>
              {checkoutError && (
                <div className="mb-3 rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {checkoutError}
                </div>
              )}
              {unlocking ? (
                <div className="py-6 flex flex-col items-center gap-4">
                  <LoadingWithFactoids
                    progressMessages={[
                      "Planning the next day of your trip...",
                      "This usually takes a couple of minutes per day...",
                      "You can leave this page and return to it later...",
                      FACTOID_WAIT_MESSAGE_WITH_OR
                    ]}
                  />
                  <p className="text-sm text-white/80 text-center max-w-md">
                    It will take several minutes to prepare the remaining days. Sit tight — this page will update automatically when your upgrade is ready.
                  </p>
                </div>
              ) : (
                <ItineraryUpgradeCta
                  isTripCapped={isTripCapped}
                  plannedDays={plannedDays}
                  totalTripDays={totalTripDays}
                  maxItineraryDays={MAX_ITINERARY_DAYS}
                  daysShown={daysShown}
                  unplannedDays={unplannedDays}
                  onUnlock={handleUpgradeClick}
                  isFreePilot={pilotActive}
                  disabled={offerLoading || unlocking}
                />
              )}

              {/* Secondary conversion content: What You Get (preview only) */}
              {!unlocking && (
                <div className="mt-8 border-t border-white/10 pt-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-semibold mb-3">What You Get</h2>
                    <p className="text-lg md:text-xl mb-2 max-w-3xl mx-auto leading-relaxed text-white">
                      Each unlocked itinerary comes with tools to refine your plan, stay organized, and share with ease.
                    </p>
                  </div>

                  {/* Featured: Iteration capability - Full width card */}
                  <div className="mb-8">
                    <div className="glass-card rounded-lg border-2 border-emerald-400/30 bg-emerald-500/5 p-8 transition-transform duration-300 ease-out hover:-translate-y-1 hover:border-emerald-400/40">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-4xl">✨</span>
                        <h3 className="text-2xl font-bold text-white">Iterate until it's perfect</h3>
                      </div>
                      <p className="text-base md:text-lg text-white/90 leading-relaxed max-w-4xl">
                        Don't love a particular day? Regenerate it with specific instructions like "more beach time" or "less driving." You get 20 regenerations to fine-tune your trip exactly how you want it.
                      </p>
                    </div>
                  </div>

                  {/* Supporting features - 3-column grid */}
                  <FeatureShowcase
                    cta={false}
                    items={[
                      {
                        title: 'Print-ready PDF',
                        body: 'Download or print a clean, one-day-per-page PDF for offline viewing. Easy to bring along or share with a travel partner.',
                        image: {
                          src: '/images/features/what-you-get/pdf-preview.png',
                          alt: 'Preview of the itinerary PDF export'
                        }
                      },
                      {
                        title: 'Calendar export',
                        body: 'Create calendar events (.ics) for every itinerary block. Adjust timing, set reminders, and invite others from the calendar you already use.',
                        image: {
                          src: '/images/features/what-you-get/calendar-export.png',
                          alt: 'Calendar view generated from the itinerary'
                        }
                      },
                      {
                        title: 'Share & bookmark',
                        body: 'Share a private link for feedback and bookmark the plan to keep everything synced across devices.',
                        image: {
                          src: '/images/features/what-you-get/share-bookmark.png',
                          alt: 'Sharing and bookmarking options for the itinerary'
                        }
                      }
                    ]}
                  />

                  <div className="mt-6 flex justify-center">
                    <button type="button" className="btn-primary-cta w-full" onClick={handleUpgradeClick}>
                      {pilotActive ? 'Get My Complete Itinerary Free' : PREVIEW_PRICE_DISPLAY}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="mb-6" />
          )}
        </div>
      )}

      {fullAccess && (
        <div className="mt-6 space-y-6">
          {extraDays.map((day) => (
            <ItineraryDayCard
              key={`day-${day.dayNumber}`}
              day={day}
              isCollapsed={collapsedSections.days?.[day.dayNumber] || false}
              onToggle={() => toggleDayCollapsed(day.dayNumber)}
              canEdit={canIterate}
              canRegenerate={canRegenerate}
              isRegenerating={isDayRegenerating(day.dayNumber)}
              onRegenerate={handleOpenRegeneration}
              onViewHistory={handleOpenHistory}
            />
          ))}
        </div>
      )}

      {showGenerationLoader && (
        <div className="mt-6 flex flex-col items-center gap-4">
          <LoadingWithFactoids
            progressMessages={[
              "Planning the next day of your trip...",
              "This usually takes a couple of minutes per day...",
              "You can leave this page and return to it later...",
              FACTOID_WAIT_MESSAGE_WITH_OR
            ]}
          />
          <p className="text-sm text-white/80 text-center max-w-md">
            {data?.meta?.unlockSource?.type === 'free_pilot'
              ? 'Your complete itinerary is free. Bookmark this page to return later; the remaining days will continue generating even if you close this tab.'
              : "It will take several minutes to prepare the remaining days. We've emailed you a link to access your itinerary, and it will continue generating even if you close this tab."}
          </p>
        </div>
      )}

      {showItineraryActions && (
        <div className="mt-10 flex justify-center">
          <ItineraryActionsBar
            labelMode="long"
            variant="primary"
            actions={itineraryActions}
            onAction={handleItineraryAction}
            busyActionKey={downloadingPdf ? 'pdf' : undefined}
            busyLabel="Preparing PDF…"
          />
        </div>
      )}

      </div>

      {showGlobalToggle && (
        <div
          className="fixed z-40"
          style={{ top: '8.5rem', right: `${globalToggleOffset}px` }}
        >
          <button
            type="button"
            onClick={handleGlobalToggle}
            aria-pressed={globalCollapsed}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/50 bg-emerald-500/15 px-4 py-2 text-xs md:text-sm font-medium text-emerald-100 shadow-lg shadow-emerald-900/20 backdrop-blur-sm transition-colors hover:bg-emerald-500/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
          >
            <span className="inline-flex items-center justify-center rounded-full bg-emerald-500/25 p-1 text-emerald-100">
              <svg
                className={`w-4 h-4 transform transition-transform duration-200 ${globalCollapsed ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M5 7h14M5 12h14M5 17h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>{globalToggleLabel}</span>
          </button>
        </div>
      )}

      <ItineraryPaymentSheet
        isOpen={paymentSheetOpen && !pilotActive}
        itineraryId={interviewId}
        versionId={versionId}
        onClose={handlePaymentClose}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />
      <EditTokenSheet
        isOpen={editTokenSheetOpen}
        onClose={() => setEditTokenSheetOpen(false)}
        onEnable={handleEnableEditing}
        viewOnlyUrl={viewOnlyUrl}
        editableUrlHint={editableUrl}
      />
      {copyToast?.message && (
        <div className="fixed bottom-6 left-1/2 z-40 w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
          <div
            className={`rounded-full border px-4 py-2 text-center text-sm font-semibold shadow-xl backdrop-blur ${copyToast.kind === 'success'
              ? 'border-emerald-400/30 bg-emerald-500/15 text-emerald-100'
              : copyToast.kind === 'error'
                ? 'border-rose-400/40 bg-rose-500/15 text-rose-100'
                : 'border-amber-400/30 bg-amber-500/15 text-amber-100'
            }`}
            role="status"
            aria-live="polite"
          >
            {copyToast.message}
          </div>
        </div>
      )}
      <DayRegenerationSheet
        isOpen={regenerationSheetOpen}
        onClose={() => setRegenerationSheetOpen(false)}
        interviewId={interviewId}
        itineraryId={itineraryId}
        versionId={versionId}
        dayIndex={regenerationDayIndex}
        dayTitle={activeRegenerationDay?.title}
        editToken={editToken}
        regenerationsRemaining={fullAccess ? regenerationsRemaining : previewRegenerationsRemaining}
        maxRegenerations={fullAccess ? regenerationMax : previewRegenerationMax}
        isPreviewMode={isPreviewMode}
        previewReady={previewGenerationComplete}
        onStart={handleRegenerationStart}
        onSuccess={handleRegenerationSuccess}
        onError={handleRegenerationError}
      />
      <DayHistoryDrawer
        isOpen={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        itineraryId={itineraryId}
        versionId={versionId}
        dayIndex={historyDayIndex}
        dayTitle={activeHistoryDay?.title}
        editToken={editToken}
        isPreviewMode={isPreviewMode}
      />
      </LayoutNoNav>
    </>
  );
}
